import { usePlayerProfileStore } from '~/stores/player-profile';

// Dono do ciclo de vida do perfil do jogador (GET /players/me): quem pergunta,
// quem re-pergunta e quem invalida.
//
// Vive num plugin, e não no componente que consome, por três motivos:
//
// 1. Os consumidores se multiplicaram — item do check-in no menu horizontal,
//    gate da roletinha e o card de VIP do Clube Fidelidade. Amarrar o disparo à
//    montagem do menu deixaria o resto na mão da sorte (no mobile, por exemplo, a
//    roletinha abre pelo badge da barra, sem o menu na tela).
// 2. A INVALIDAÇÃO não tem componente nenhum: quem troca de conta ou sai não
//    passa necessariamente por uma tela que saiba disso. O `stores/auth.ts` é
//    arquivo da base Cactus e não recebe edição do fork, então o gancho é aqui —
//    e observar o `userId` cobre mais que o logout: cobre trocar de conta na
//    mesma aba, que o logout sozinho não pega.
// 3. Um listener só. Antes o `visibilitychange` era registrado pelo menu; com
//    vários consumidores seriam vários listeners fazendo a mesma pergunta.
export default defineNuxtPlugin(() => {
  const playerProfileStore = usePlayerProfileStore();
  const { userId } = useUserData();

  // O perfil aterrissa DEPOIS da sessão (o `userId` vem do /auth/user-profile),
  // então quem manda no disparo é o id e não o `isAuthenticated`: com o watch no
  // id, a pergunta sai no frame em que ele chega — login pelo modal, sem
  // recarregar a página, incluído.
  watch(
    userId,
    (id, previousId) => {
      // Sair da conta (id → 0) ou trocar de conta: o que se sabia era do jogador
      // anterior. Limpa inclusive o memo desta aba, senão o "sim" dele ficaria
      // valendo pra quem entrar depois no mesmo navegador.
      if (previousId && previousId !== id) {
        playerProfileStore.reset(previousId);
      }

      if (id) playerProfileStore.ensure();
    },
    { immediate: true },
  );

  // O jogador vira inelegível pra elegível apostando — e apostar acontece FORA
  // desta tela (jogo em outra view, iframe, outra aba). Voltar o foco pra cá é o
  // sinal mais próximo de "pode ter mudado" que existe sem webhook do provedor, e
  // custa uma pergunta que quase sempre nem sai da máquina.
  //
  // Pergunta em TODO foco, inclusive pra quem já é elegível: a elegibilidade em
  // si não muda mais (aposta não se desfaz), mas o nível VIP da mesma resposta
  // muda — e era justamente a guarda de `isEligible` aqui, somada ao memo do
  // handler, que fazia o tier congelar até o jogador abrir uma aba nova.
  //
  // Sem polling por intervalo: o gatilho é o foco. O que filtra o volume é o
  // tempo que a aba passou ESCONDIDA, não um freio no relógio: ir e voltar num
  // alt-tab não é sinal de nada, porque abrir um jogo e fazer uma aposta em outra
  // aba leva muito mais que meio minuto. Abaixo desse piso a pergunta nem sai.
  //
  // Isto é diferente do freio de 60s que existia no handler: aquele calava a
  // pergunta pelo relógio, inclusive a primeira depois de uma ausência longa (e
  // com ela o nível VIP). Aqui a primeira volta de verdade sempre pergunta.
  const MIN_HIDDEN_MS = 30 * 1000;

  let hiddenAt: number | null = null;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') {
      hiddenAt = Date.now();

      return;
    }

    if (!userId.value) return;

    const hiddenFor = hiddenAt === null ? Infinity : Date.now() - hiddenAt;

    hiddenAt = null;

    // `Infinity` cobre o primeiro evento de visibilidade da carga da página (sem
    // esconder antes): aí a pergunta sai, e a duplicata com a do login morre na
    // janela de dedupe do handler.
    if (hiddenFor < MIN_HIDDEN_MS) return;

    playerProfileStore.ensure();
  });
});
